import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { supabase, TABLES } from '../../config/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import toast from 'react-hot-toast'

const { FiX, FiTrash2, FiAlertTriangle } = FiIcons

const DeleteChildModal = ({ isOpen, onClose, child, onChildDeleted }) => {
  const { family } = useAuth()
  const [loading, setLoading] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const handleDelete = async () => {
    if (!child || !family) {
      toast.error('Child or family not found. Please refresh and try again.')
      return
    }

    if (confirmText.toLowerCase() !== child.name.toLowerCase()) {
      toast.error(`Please type "${child.name}" to confirm deletion.`)
      return
    }

    setLoading(true)
    
    try {
      console.log('Deleting child:', child.id)

      // Delete the child (this will cascade to related records due to foreign key constraints)
      const { error } = await supabase
        .from(TABLES.CHILDREN)
        .delete()
        .eq('id', child.id)
        .eq('family_id', family.id) // Ensure user can only delete their family's children

      if (error) {
        console.error('Child deletion error:', error)
        toast.error(`Failed to delete child: ${error.message || 'Unknown error'}`)
      } else {
        console.log('Child deleted successfully')
        toast.success(`${child.name} has been removed from your family. 👋`)
        onChildDeleted?.(child.id)
        onClose()
        setConfirmText('')
      }

    } catch (error) {
      console.error('Unexpected error deleting child:', error)
      toast.error('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setConfirmText('')
    onClose()
  }

  if (!child) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-2xl shadow-xl w-full max-w-md"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Remove Child</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="p-2"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </Button>
              </div>

              {/* Child Preview */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border-l-4 border-red-500">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-3xl">{child.avatar || '👦'}</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">{child.name}</h3>
                    {child.age && (
                      <p className="text-sm text-gray-600">Age {child.age}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Warning Message */}
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-lg mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-red-800 mb-1">
                      Are you sure you want to remove {child.name}?
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      <li>• This action cannot be undone</li>
                      <li>• {child.name} will be permanently removed from your family</li>
                      <li>• All task completions and progress will be preserved for records</li>
                      <li>• Any tasks assigned to {child.name} will become unassigned</li>
                      <li>• {child.name} will no longer be able to access the family account</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Confirmation Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To confirm, type <strong>"{child.name}"</strong> below:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={`Type "${child.name}" to confirm`}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  className="flex-1"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  loading={loading}
                  className="flex-1"
                  disabled={confirmText.toLowerCase() !== child.name.toLowerCase()}
                >
                  <SafeIcon icon={FiTrash2} className="text-sm" />
                  {loading ? 'Removing...' : 'Remove Child'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default DeleteChildModal